import { useMemo } from 'react';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle,
  XCircle,
  ChevronRight,
  Map,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useUpdateHospital, useDeactivateHospital } from '../hooks';
import { CreateHospitalModal } from '../components';
import { HospitalDetailModal } from '../components/HospitalDetailModal';
import toast from 'react-hot-toast';
import { useModal } from '@/components/modal';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../../admin/utils/adminAnimations';
import { GlassCard } from '@/components/glass';
import { localizedName } from '@/utils/localized';
import type { HospitalListResponse } from '../types';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export function HospitalsPage() {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const isRTL = useRTL();

  const { hasPermission } = usePermissions();

  const queryClient = useQueryClient();

  const [params, setParams] = useQueryParams({
    q: { default: '' },
    status: { default: '' },
    hospital: { default: '' },
    action: { default: '' },
  });

  const updateHospitalMutation = useUpdateHospital();
  const deleteHospitalMutation = useDeactivateHospital();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['hospitals', 'infinite', params.q, params.status],
    queryFn: async ({ pageParam }) => {
      const queryParams: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (params.q) queryParams.search = params.q;
      if (params.status) queryParams.isActive = params.status;

      const { data } = await http.get<HospitalListResponse>('/hospitals', { params: queryParams });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.hospitals.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allHospitals = useMemo(
    () => data?.pages.flatMap((page) => page.hospitals) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allHospitals);

  const selectedHospital = useMemo(
    () => (params.hospital ? allHospitals.find(h => h.id === params.hospital) ?? null : null),
    [allHospitals, params.hospital],
  );

  const createModalOpen = params.action === 'create';

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allHospitals.filter((h) => h.isActive).length,
    [allHospitals]
  );
  const inactiveCount = useMemo(
    () => allHospitals.filter((h) => !h.isActive).length,
    [allHospitals]
  );
  const totalTherapists = useMemo(
    () => allHospitals.reduce((sum, h) => sum + (h.therapistCount ?? 0), 0),
    [allHospitals]
  );

  const handleBulkActivate = async () => {
    try {
      triggerHapticFeedback();
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          updateHospitalMutation.mutateAsync({ id, data: { isActive: true } })
        )
      );
      toast.success(t('hospitals.bulkActivatedSuccess'));
      clear();
    } catch {
      toast.error(t('hospitals.bulkActivateFailed'));
    }
  };

  const handleBulkDeactivate = async () => {
    try {
      triggerHapticFeedback();
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          updateHospitalMutation.mutateAsync({ id, data: { isActive: false } })
        )
      );
      toast.success(t('hospitals.bulkDeactivatedSuccess'));
      clear();
    } catch {
      toast.error(t('hospitals.bulkDeactivateFailed'));
    }
  };

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('hospitals.bulkDeleteTitle'),
      message: t('hospitals.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteHospitalMutation.mutateAsync(id)));
        toast.success(t('hospitals.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <PageHeader
        icon={<Building2 className="w-7 h-7" />}
        title={t('hospitals.management')}
        subtitle={t('hospitals.managementSubtitle', { defaultValue: 'Manage hospital locations' })}
        action={
          <>
            <motion.a
              whileTap={{ scale: 0.95 }}
              href="/admin/hospitals/map"
              className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 border border-white/30 rounded-xl font-bold hover:translate-y-[-2px] transition-transform cursor-pointer"
              style={{
                backgroundColor: ADMIN_COLORS.surface,
                border: `1px solid ${ADMIN_COLORS.border}`,
                boxShadow: ADMIN_COLORS.shadow,
                color: ADMIN_COLORS.navy,
              }}
            >
              <Map className="w-5 h-5" />
              <span className="hidden md:inline">{t('hospitals.mapView')}</span>
            </motion.a>
            {hasPermission('action:manage_hospitals') && (
              <CreateButton
                label={t('hospitals.createHospital')}
                onClick={() => setParams({ action: 'create' })}
              />
            )}
          </>
        }
      />

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Building2 className="w-full h-full" />} label={t('hospitals.totalHospitals')} value={totalCount} />
        <StatCard icon={<CheckCircle className="w-full h-full" />} label={t('hospitals.activeHospitals')} value={activeCount} />
        <StatCard icon={<XCircle className="w-full h-full" />} label={t('hospitals.inactiveHospitals')} value={inactiveCount} />
        <StatCard icon={<Users className="w-full h-full" />} label={t('hospitals.assignedEmployeesCount')} value={totalTherapists} />
      </motion.div>

      {/* ─── Search & Filters ────────────────────────────────── */}
      <motion.div variants={staggerItem}>
        <GlassCard title={t('common.filters', { defaultValue: 'Filters' })}>
        <div className="flex flex-col md:flex-row gap-3">
          <SearchInput
            value={params.q}
            onChange={(v) => setParams({ q: v })}
            placeholder={t('hospitals.searchPlaceholder')}
          />

          <button
            onClick={selectAll}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={selectAll}
              className="w-5 h-5 pointer-events-none"
              readOnly
            />
            <span className="font-bold text-sm">{t('hospitals.selectAll')}</span>
          </button>

          <GlassSelect
            value={params.status}
            onChange={(e) => {
              setParams({ status: e.target.value });
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('hospitals.allStatuses')}</option>
            <option value="true">{t('hospitals.active')}</option>
            <option value="false">{t('hospitals.inactive')}</option>
          </GlassSelect>
        </div>
        </GlassCard>
      </motion.div>

      {/* ─── Hospital List ───────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('hospitals.loadError')} />
        ) : allHospitals.length === 0 ? (
          <EmptyState
            icon={<Building2 className="w-full h-full" />}
            message={t('hospitals.noHospitals')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allHospitals.map((hospital) => {
              const selected = isSelected(hospital.id);

              return (
                <motion.div
                  key={hospital.id}
                  role="button"
                  tabIndex={0}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    triggerHapticFeedback();
                    setParams({ hospital: hospital.id });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      triggerHapticFeedback();
                      setParams({ hospital: hospital.id });
                    }
                  }}
                  className={`glass-surface flex items-center justify-between w-full py-2.5 px-3 md:py-3 md:px-4 rounded-3xl text-start cursor-pointer border border-white/30 hover:bg-white/20 ${selected ? 'bg-white/20' : ''}`}
                  style={{
                    boxShadow: ADMIN_COLORS.shadow,
                  }}
                >
                  {/* Left section */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Checkbox — large tap target */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                      style={{ minWidth: '44px', minHeight: '44px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(hospital.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggle(hospital.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-5 h-5 pointer-events-none"
                      />
                    </div>

                    {/* Building icon circle */}
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: ADMIN_COLORS.ice,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <Building2 className="w-5 h-5 md:w-6 md:h-6" />
                    </div>

                    {/* Text content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-800 text-sm md:text-base truncate max-w-[160px] md:max-w-none">
                        {localizedName(hospital, i18n.language)}
                      </p>
                    </div>
                  </div>

                  {/* Right section */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Therapist count (desktop only) */}
                    <div className="hidden md:flex items-center gap-1 text-sm font-medium text-neutral-800">
                      <Users className="w-4 h-4" />
                      {hospital.therapistCount ?? 0}
                    </div>

                    {/* Status dot */}
                    <StatusDot
                      active={hospital.isActive}
                      title={hospital.isActive ? t('hospitals.active') : t('hospitals.inactive')}
                    />

                    {/* Chevron */}
                    <ChevronRight
                      className="w-5 h-5 text-black/40"
                      style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Infinite scroll sentinel ─────────────────────────── */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />

      {/* ─── Modals ───────────────────────────────────────────── */}
      <CreateHospitalModal
        isOpen={createModalOpen}
        onClose={() => { queryClient.refetchQueries({ queryKey: ['hospitals'] }); setParams({ action: '' }); }}
        onSuccess={() => {}}
      />

      {selectedHospital && (
        <HospitalDetailModal
          hospital={selectedHospital}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['hospitals'] }); setParams({ hospital: '' }); }}
        />
      )}

      {/* ─── Bulk Action Bar — Mushroom buttons ───────────────── */}
      <BulkActionBar
        count={count}
        onDelete={handleBulkDelete}
        onClear={clear}
        isDeleting={deleteHospitalMutation.isPending}
        onDeactivate={handleBulkDeactivate}
        deactivateLabel={t('hospitals.inactive')}
        isDeactivating={updateHospitalMutation.isPending}
        onActivate={handleBulkActivate}
        activateLabel={t('hospitals.active')}
        isActivating={updateHospitalMutation.isPending}
      />
    </motion.div>
  );
}
